import type { ApiResponse } from "../../utils/apiClient";
import { adminApi, adminSilentApi, userSilentApi } from "../../utils/apiInstances";

export type AuthContext = "admin" | "user";
type QueryValue = string | number | boolean | null | undefined;
type BlogPayload = Record<string, unknown>;

/**
 * Unified function to fetch blogs with parameters.
 */
export const handleGetBlogs = async <TData = unknown>(
    context: AuthContext,
    params: Record<string, QueryValue> = {}
): Promise<ApiResponse<TData>> => {
    try {
        const queryString = new URLSearchParams(
            Object.entries(params).reduce<Record<string, string>>((acc, [key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                    acc[key] = String(value);
                }
                return acc;
            }, {})
        ).toString();
        const api = context === "admin" ? adminSilentApi : userSilentApi;
        const baseEndpoint = context === "admin" ? "/admin/admin-blogs" : "/user/blogs";
        const querySuffix = queryString ? `?${queryString}` : "";
        const endpoint = `${baseEndpoint}${querySuffix}`;

        const response = await api<TData>("GET", endpoint);
        return response;
    } catch (error) {
        console.error(`Error fetching blogs (${context}):`, error);
        throw error;
    }
};

/**
 * Unified function to fetch a single blog by ID.
 */
export const handleGetBlogById = async <TData = unknown>(
    context: AuthContext,
    id: string | number
): Promise<ApiResponse<TData>> => {
    try {
        const api = context === "admin" ? adminSilentApi : userSilentApi;
        const baseEndpoint = context === "admin" ? "/admin/admin-blogs" : "/user/blogs";
        const response = await api<TData>("GET", `${baseEndpoint}/${id}`);
        return response;
    } catch (error) {
        console.error(`Error fetching blog by ID (${context}):`, error);
        throw error;
    }
};

/**
 * Admin-only: Create a new blog post.
 */
export const handleCreateBlog = async (
    userData: BlogPayload,
    onSuccess: (response: ApiResponse) => void,
    onError?: (error: unknown) => void
) => {
    try {
        const response = await adminApi("POST", "/admin/admin-blogs", userData);
        onSuccess(response);
    } catch (error) {
        if (onError) onError(error);
    }
};

/**
 * Admin-only: Update an existing blog post.
 */
export const handleUpdateBlog = async (
    id: string | number,
    userData: BlogPayload,
    onSuccess: (response: ApiResponse) => void,
    onError?: (error: unknown) => void
) => {
    try {
        const response = await adminApi("PUT", `/admin/admin-blogs/${id}`, userData);
        onSuccess(response);
    } catch (error) {
        if (onError) onError(error);
    }
};

/**
 * Admin-only: Delete a blog post.
 */
export const handleDeleteBlog = async (
    id: string | number,
    onSuccess: (response: ApiResponse) => void,
    onError?: (error: unknown) => void
) => {
    try {
        const response = await adminApi("DELETE", `/admin/admin-blogs/${id}`);
        onSuccess(response);
    } catch (error) {
        if (onError) onError(error);
    }
};

/**
 * User-only: Get application settings.
 * (Kept here for compatibility with original user blog controller)
 */
export const handleGetAppSettings = async () => {
    try {
        const response = await userSilentApi("GET", "/user/app-settings");
        return response;
    } catch (error) {
        console.error("Error fetching app settings:", error);
        throw error;
    }
};
/**
 * User-only: Get items with params (Legacy support).
 */
export const handleGetItemsWithParam = async <TData = unknown>(
    params: Record<string, QueryValue> = {}
): Promise<ApiResponse<TData>> => {
    try {
        const queryParams = new URLSearchParams(
            Object.entries(params).reduce<Record<string, string>>((acc, [key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                    acc[key] = String(value);
                }
                return acc;
            }, {})
        ).toString();
        const querySuffix = queryParams ? `?${queryParams}` : "";
        const response = await userSilentApi<TData>("GET", `/user/items${querySuffix}`);
        return response;
    } catch (error) {
        console.error("Error fetching items with params:", error);
        throw error;
    }
};
